import { useEffect, useMemo, useState } from "react";
import type { BankQuestionDetail } from "../../../shared/types";
import { Ico } from "../../components/shell";
import { Stage } from "../../components/primitives";
import { StateLoading } from "../states/States";
import {
  QuestionForm,
  blankQuestion,
  questionIssue,
  bankToDraft,
  draftToInput,
  type DraftQuestion,
} from "../../components/QuestionForm";
import { AtlShell, AtlSearch, XIcon } from "../../components/atelier";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";

const TYPE_LABEL: Record<string, string> = { mc: "MC", truefalse: "T/F", multi: "Multi" };
const CAT_COLORS = ["var(--atl-a)", "var(--atl-b)", "var(--atl-c)", "var(--atl-d)"];

function lastUsed(ts: number): string {
  const days = Math.max(0, Math.round((Date.now() - ts) / 86_400_000));
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

type EditTarget = { id: string | null; draft: DraftQuestion } | null;

export default function QuestionBank() {
  const [questions, setQuestions] = useState<BankQuestionDetail[] | null>(null);
  const [cat, setCat] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [edit, setEdit] = useState<EditTarget>(null);
  const [saving, setSaving] = useState(false);

  function reload() {
    api.listBank().then((r) => setQuestions(r.questions)).catch(() => setQuestions([]));
  }
  useEffect(reload, []);

  const cats = useMemo(() => {
    const m = new Map<string, number>();
    for (const q of questions ?? []) {
      const c = q.category || "Uncategorised";
      m.set(c, (m.get(c) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [questions]);

  const colorFor = (c: string) => CAT_COLORS[Math.max(0, cats.findIndex(([n]) => n === c)) % CAT_COLORS.length];

  const filtered = (questions ?? []).filter((q) => {
    if (cat && (q.category || "Uncategorised") !== cat) return false;
    if (query && !q.prompt.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  async function saveEdit() {
    if (!edit) return;
    const issue = questionIssue(edit.draft);
    if (issue) {
      alertDialog({ title: "Not quite ready", message: `This question ${issue}.` });
      return;
    }
    setSaving(true);
    try {
      const input = draftToInput(edit.draft);
      if (edit.id) await api.updateBankQuestion(edit.id, input);
      else await api.createBankQuestion(input);
      setEdit(null);
      reload();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not save the question");
    } finally {
      setSaving(false);
    }
  }

  async function del(id: string) {
    const ok = await confirmDialog({
      title: "Delete question?",
      message: "This question will be removed from the bank. Quizzes already built from it are not affected.",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteBankQuestion(id);
      reload();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not delete the question");
    }
  }

  if (questions === null)
    return (
      <Stage>
        <div className="atl" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
          <StateLoading />
        </div>
      </Stage>
    );

  return (
    <Stage>
      <AtlShell
        active="Question Bank"
        title="Question Bank"
        sub={`${questions.length} REUSABLE QUESTION${questions.length === 1 ? "" : "S"}`}
        actions={
          <>
            <AtlSearch placeholder="Filter by keyword…" w={230} value={query} onChange={setQuery} />
            <button className="atl-btn atl-btn--primary" onClick={() => setEdit({ id: null, draft: blankQuestion() })}>
              <Ico name="plus" size={16} /> New question
            </button>
          </>
        }
      >
        <div className="atl-twopane" style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* LEFT — categories + type filter */}
          <div
            className="atl-twopane__rail"
            style={{
              width: 226,
              flex: "0 0 226px",
              borderRight: "1px solid var(--atl-line)",
              padding: 22,
              background: "var(--atl-surface)",
              overflowY: "auto",
            }}
          >
            <div className="atl-eyebrow" style={{ marginBottom: 14 }}>
              CATEGORIES
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <CatRow label="All questions" count={questions.length} on={cat === null} onClick={() => setCat(null)} />
              {cats.map(([c, n]) => (
                <CatRow key={c} label={c} count={n} on={cat === c} onClick={() => setCat(c)} />
              ))}
              {cats.length === 0 && (
                <div style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500, padding: "0 12px" }}>None yet</div>
              )}
            </div>

            <hr className="atl-rule" style={{ margin: "20px 0", background: "var(--atl-line-2)" }} />

            <div className="atl-eyebrow" style={{ marginBottom: 12 }}>
              TYPE
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {["Multiple choice", "True / false", "Multi-select"].map((t) => (
                <span key={t} className="atl-chip" style={{ fontSize: 12 }}>
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — toolbar + question rows */}
          <div style={{ flex: 1, padding: "20px 26px", display: "flex", flexDirection: "column", minWidth: 0, gap: 9, overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
              <span className="atl-chip atl-chip--accent">
                {cat ?? "All questions"} · {filtered.length}
              </span>
              <div style={{ flex: 1 }} />
              <span className="atl-eyebrow">CLICK A QUESTION TO EDIT</span>
            </div>

            {filtered.length === 0 ? (
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  flex: 1,
                  color: "var(--atl-soft)",
                  fontWeight: 500,
                  textAlign: "center",
                  gap: 12,
                }}
              >
                <div>
                  Your bank is empty.
                  <br />
                  <span className="atl-link2" onClick={() => setEdit({ id: null, draft: blankQuestion() })}>
                    Add your first question →
                  </span>
                </div>
              </div>
            ) : (
              filtered.map((r) => {
                const c = r.category || "Uncategorised";
                return (
                  <div key={r.id} className="atl-row" style={{ padding: "12px 16px", gap: 13 }}>
                    <div
                      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => setEdit({ id: r.id, draft: bankToDraft(r) })}
                    >
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: 14.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.prompt || "Untitled question"}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 5, alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: colorFor(c) }}>● {c}</span>
                        <span className="atl-mono" style={{ fontSize: 10.5, color: "var(--atl-faint)" }}>
                          {r.points.toLocaleString()} pts
                        </span>
                        <span className="atl-mono" style={{ fontSize: 10.5, color: "var(--atl-faint)" }}>
                          {r.timeLimit}s
                        </span>
                      </div>
                    </div>
                    <span
                      className="atl-mono"
                      style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 99,
                        border: "1px solid var(--atl-line)",
                        color: "var(--atl-soft)",
                        width: 60,
                        textAlign: "center",
                      }}
                    >
                      {TYPE_LABEL[r.type] ?? r.type}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--atl-faint)", width: 64, textAlign: "right" }}>
                      {lastUsed(r.updatedAt)}
                    </span>
                    <button
                      className="atl-iconbtn atl-iconbtn--danger"
                      title="Delete"
                      style={{ width: 34, height: 34, flex: "0 0 auto" }}
                      onClick={() => del(r.id)}
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </AtlShell>

      {edit && (
        <div
          className="atl"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(43,30,20,.42)",
            display: "grid",
            placeItems: "center",
            zIndex: 50,
            padding: 24,
          }}
          onClick={() => !saving && setEdit(null)}
        >
          <div
            className="atl-card"
            style={{
              width: "min(680px, 100%)",
              maxHeight: "90vh",
              overflow: "auto",
              padding: 26,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              boxShadow: "0 30px 80px rgba(40,25,15,.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2 className="atl-serif" style={{ fontSize: 21, flex: 1 }}>
                {edit.id ? "Edit question" : "New bank question"}
              </h2>
              <button className="atl-iconbtn" style={{ border: "none" }} onClick={() => !saving && setEdit(null)}>
                <XIcon size={15} />
              </button>
            </div>
            <QuestionForm question={edit.draft} onChange={(nq) => setEdit({ id: edit.id, draft: nq })} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="atl-btn atl-btn--ghost" disabled={saving} onClick={() => setEdit(null)}>
                Cancel
              </button>
              <button className="atl-btn atl-btn--primary" disabled={saving} onClick={saveEdit}>
                <Ico name="check" size={16} /> {saving ? "Saving…" : "Save to bank"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Stage>
  );
}

function CatRow({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "10px 12px",
        borderRadius: 9,
        cursor: "pointer",
        background: on ? "#fbf2f3" : "transparent",
        boxShadow: on ? "inset 2px 0 0 var(--atl-accent)" : "none",
      }}
    >
      <span
        style={{
          fontSize: 13.5,
          fontWeight: on ? 600 : 500,
          color: on ? "var(--atl-accent)" : "var(--atl-soft)",
          flex: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      <span className="atl-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--atl-faint)" }}>
        {count}
      </span>
    </div>
  );
}
